import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, X, Plus } from "lucide-react";
import PlanBadge from "./PlanBadge";

interface PhotoGalleryProps {
  isPremium?: boolean;
  isBasic?: boolean;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ isPremium = false, isBasic = false }) => {
  const [images, setImages] = useState<{ id: number; src: string; type: 'logo' | 'cover' | 'gallery' }[]>([
    { id: 1, src: 'https://images.unsplash.com/photo-1584564515303-6ffe578dbf77?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80', type: 'logo' },
    { id: 2, src: 'https://images.unsplash.com/photo-1581349485608-9469926a8e5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80', type: 'cover' },
    { id: 3, src: 'https://images.unsplash.com/photo-1584564518295-2b88e8e1edb7?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80', type: 'gallery' },
  ]);

  const [dragOver, setDragOver] = useState(false);
  
  // Determine user's plan
  const userPlan = isPremium ? 'premium' : (isBasic ? 'basic' : 'free');

  // Calculate how many images are allowed based on plan
  const maxImages = isPremium ? 10 : (isBasic ? 3 : 0);
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newImages = Array.from(e.target.files).map((file, index) => ({
        id: Date.now() + index,
        src: URL.createObjectURL(file),
        type: 'gallery' as const
      }));
      
      setImages([...images, ...newImages]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newImages = Array.from(e.dataTransfer.files).map((file, index) => ({
        id: Date.now() + index,
        src: URL.createObjectURL(file),
        type: 'gallery' as const
      }));
      
      setImages([...images, ...newImages]);
    }
  };

  const removeImage = (id: number) => {
    setImages(images.filter(image => image.id !== id));
  };

  const logoImage = images.find(img => img.type === 'logo');
  const coverImage = images.find(img => img.type === 'cover');
  const galleryImages = images.filter(img => img.type === 'gallery');
  
  const imagesLeft = maxImages - galleryImages.length;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle>Photos & Branding</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Logo Image */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Profile Image (Logo)</label>
              <PlanBadge requiredPlan="free" userPlan={userPlan} tooltip="Available on all plans" />
            </div>
            <div className="flex items-center">
              {logoImage ? (
                <div className="relative w-20 h-20 rounded-md overflow-hidden">
                  <img 
                    src={logoImage.src} 
                    alt="Logo" 
                    className="w-full h-full object-cover"
                  />
                  <button
                    className="absolute top-1 right-1 bg-black bg-opacity-60 rounded-full p-1 text-white hover:bg-opacity-80"
                    onClick={() => removeImage(logoImage.id)}
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                  <label htmlFor="logo-upload" className="cursor-pointer">
                    <Plus size={24} className="text-gray-500" />
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                  </label>
                </div>
              )}
              <div className="ml-4 text-sm text-gray-500">
                <p>Upload your business logo</p>
                <p>Recommended size: 500x500px</p>
              </div>
            </div>
          </div>

          {/* Cover Image */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Cover Image (Hero)</label>
              <PlanBadge requiredPlan="free" userPlan={userPlan} tooltip="Available on all plans" />
            </div>
            <div className="flex flex-col">
              {coverImage ? (
                <div className="relative w-full h-40 rounded-md overflow-hidden">
                  <img 
                    src={coverImage.src} 
                    alt="Cover" 
                    className="w-full h-full object-cover"
                  />
                  <button
                    className="absolute top-2 right-2 bg-black bg-opacity-60 rounded-full p-1.5 text-white hover:bg-opacity-80"
                    onClick={() => removeImage(coverImage.id)}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="w-full h-40 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                  <label htmlFor="cover-upload" className="cursor-pointer flex flex-col items-center">
                    <Upload size={24} className="text-gray-500 mb-2" />
                    <span className="text-sm text-gray-500">Upload cover image</span>
                    <input
                      id="cover-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                  </label>
                </div>
              )}
              <p className="mt-2 text-xs text-gray-500">Recommended size: 1200x400px</p>
            </div>
          </div>

          {/* Gallery Images */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <label className="block text-sm font-medium text-gray-700">Gallery Images</label>
                {userPlan === 'free' ? (
                  <PlanBadge requiredPlan="basic" userPlan={userPlan} tooltip="Upgrade to Basic or Premium to add gallery images" />
                ) : userPlan === 'basic' ? (
                  <PlanBadge requiredPlan="basic" userPlan={userPlan} tooltip="Basic plan includes up to 3 gallery images" />
                ) : (
                  <PlanBadge requiredPlan="premium" userPlan={userPlan} tooltip="Premium plan includes 10+ gallery images" />
                )}
              </div>
              <span className={`text-xs ${imagesLeft <= 1 && !isPremium ? 'text-red-500' : 'text-gray-500'}`}>
                {galleryImages.length}/{maxImages} used
              </span>
            </div>
            
            {userPlan === 'free' ? (
              <div className="border-2 border-gray-200 rounded-md p-6 bg-gray-50 text-center">
                <p className="text-gray-500 mb-3">Gallery images are available on Basic and Premium plans</p>
                <Button variant="outline" className="bg-sauna-blue/10 text-sauna-blue border-sauna-blue/20">
                  Upgrade to Add Gallery Images
                </Button>
              </div>
            ) : (
              <div
                className={`border-2 ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-dashed border-gray-300'} rounded-md p-4 transition-all`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-4">
                  {galleryImages.map((image) => (
                    <div key={image.id} className="relative aspect-square rounded overflow-hidden">
                      <img
                        src={image.src}
                        alt="Gallery"
                        className="w-full h-full object-cover"
                      />
                      <button
                        className="absolute top-1 right-1 bg-black bg-opacity-60 rounded-full p-1 text-white hover:bg-opacity-80"
                        onClick={() => removeImage(image.id)}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  
                  {imagesLeft > 0 && (
                    <div className="border-2 border-dashed border-gray-300 rounded aspect-square flex flex-col items-center justify-center bg-gray-50">
                      <label htmlFor="gallery-upload" className="cursor-pointer flex flex-col items-center">
                        <Plus size={24} className="text-gray-500 mb-1" />
                        <span className="text-xs text-gray-500">Add image</span>
                        <input
                          id="gallery-upload"
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-center">
                  <p className="text-xs text-gray-500 text-center">
                    Drop images here or click to upload
                  </p>
                </div>
              </div>
            )}

            {userPlan === 'basic' && imagesLeft <= 1 && (
              <div className="mt-4 bg-orange-50 border border-orange-200 rounded p-3 text-sm text-orange-800">
                <p>Upgrade to Premium to add more than 3 gallery images!</p>
              </div>
            )}
          </div>

          {/* Preview section */}
          <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Directory Preview</h3>
            <div className="aspect-[4/3] max-w-xs mx-auto bg-white shadow rounded-md overflow-hidden border border-gray-200">
              {coverImage && (
                <div className="h-1/2 bg-gray-200">
                  <img 
                    src={coverImage.src} 
                    alt="Preview cover" 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-3">
                <div className="flex items-center">
                  {logoImage && (
                    <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
                      <img 
                        src={logoImage.src} 
                        alt="Preview logo" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="ml-3">
                    <h4 className="font-medium text-gray-900">Your Business Name</h4>
                    <p className="text-xs text-gray-500">Kerry County, Ireland</p>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-xs text-gray-500 line-clamp-2">Business description preview will appear here...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PhotoGallery;
